import React, {
  memo, useRef, useState, useEffect,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import useTranslation from '../../../utils/translation-utils';
import './tag-input.scss';
import KeyEventValues from '../../../constants/key-event-values';
import { getMultipleValidations } from '../../../utils/validation-util';
import {
  MAX_ITEMS,
  MAX_LABEL_LENGTH,
  FOCUS_TIMER_INTERVAL,
  DEFAULT_LABEL_ACCESSOR, DEFAULT_ID_ACCESSOR,
} from './constants';
import TagWithSuggestions from './views/tag-with-suggestions/tag-with-suggestions';
import AnimateHeight from '../animate-height/animate-height';
import { DefaultRegexMessageMap } from '../input';

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
const TagInput = React.forwardRef(({
  className, label, isDisabled, tags, onAdd, onDelete, suggestions, maxItems, maxLabelLength,
  allowTagCreation, labelAccessor, validations, memorizeTags, validationErrorMessages,
  caseSensitive, idAccessor, rightAddOn, helperText, regexMessageMap, ...props
}, ref) => {
  const inputRef = useRef();
  const wrapperRef = useRef();
  const [getTranslation] = useTranslation();
  const [value, setValue] = useState('');
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [contextMenuItems, setContextMenuItems] = useState(suggestions);
  const openState = useRef(false);

  useEffect(() => {
    setContextMenuItems(suggestions.filter(({ id }) => (
      !tags.find(({ id: tagId }) => tagId === id))));
  }, [suggestions]);

  const getDuplicateTag = (selectedLabel) => (
    tags.find((tag) => {
      if (caseSensitive) {
        return tag[idAccessor] === selectedLabel;
      }
      return tag[idAccessor]?.toLowerCase() === selectedLabel?.toLowerCase();
    })
  );

  const checkAndUpdateValidations = (selectedLabel) => {
    let error = '';
    const regexMap = { ...DefaultRegexMessageMap, ...regexMessageMap };

    if (tags.length === maxItems) {
      error = validationErrorMessages.max_items
        || getTranslation('MSG_SELECT_ITEMS_LIMIT', { number: maxItems });
    } else if (selectedLabel.length > maxLabelLength) {
      error = getTranslation('MSG_VAL_TEXT_MAX_CHAR_COUNT', {
        text_max_char_count: maxLabelLength,
      });
    } else if (getDuplicateTag(selectedLabel)) {
      error = validationErrorMessages.text_duplicate_item
        || getTranslation('DUPLICATE_ITEM_FOUND');
    } else if (validations.length > 0) {
      const errors = getMultipleValidations(
        selectedLabel,
        validations,
        validationErrorMessages,
        regexMap,
      );

      if (errors.length > 0) {
        error = regexMap[errors].message;
      }
    }

    setValidationError(error);
    return error;
  };

  const openDropdown = () => {
    inputRef.current.click();
    openState.current = true;
  };

  const closeDropdown = () => {
    wrapperRef.current.click();
  };

  const handleOnSelect = (id, enterClicked = false) => {
    const selectedItem = contextMenuItems.find(({ id: itemId }) => id === itemId) || {};
    const isInvalid = !!checkAndUpdateValidations(selectedItem[idAccessor] || id);

    if (isInvalid || (enterClicked && !allowTagCreation)) {
      closeDropdown();
      setValue('');
      setTimeout(() => {
        inputRef.current.focus();
      }, FOCUS_TIMER_INTERVAL);
      return;
    }

    if (Object.keys(selectedItem).length === 0) {
      setValue('');
      onAdd({
        id,
        [labelAccessor]: id,
      });
      setContextMenuItems(suggestions.filter((suggestion) => (
        !tags.find(({ id: tagId }) => tagId === suggestion.id))));
    } else {
      onAdd(selectedItem);
      setValue('');
      setContextMenuItems(suggestions.filter((suggestion) => (
        ![...tags, selectedItem].find(({ id: tagId }) => tagId === suggestion.id)
      )));
      setTimeout(() => {
        inputRef.current.focus();
      }, FOCUS_TIMER_INTERVAL);
    }
  };

  const handleInputOnChange = (e) => {
    const val = e.target.value.replace(',', '');
    setValue(val);
    setContextMenuItems(suggestions.filter((tag) => (
      tag[labelAccessor].toLowerCase().includes(val.toLowerCase()))));
  };

  const handleDelete = (id) => {
    onDelete(id);
    setValidationError('');
    setDeleteIndex(null);
    inputRef.current.focus();

    if (memorizeTags) {
      const itemToBeRestored = tags.find(({ id: tagId }) => tagId === id);
      const filteredSuggestions = suggestions.filter((suggestion) => (
        !tags.find(({ id: tagId }) => tagId === suggestion.id)));
      setContextMenuItems([...filteredSuggestions, itemToBeRestored]);
    }
  };

  // Set delete tag index
  const triggerDeleteMode = () => {
    if (deleteIndex === null) {
      setDeleteIndex(tags.length - 1);
      inputRef.current.blur();
      wrapperRef.current.focus();
    } else {
      setDeleteIndex(null);
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (!openState.current) {
      openDropdown();
    }
    const { key, target: { selectionStart, selectionEnd, value: inputValue } } = event;
    const isInitial = (selectionStart === 0 && selectionEnd === 0) || selectionStart === undefined;

    switch (key) {
      case KeyEventValues.ARROW_LEFT.key:
        if (!isInitial) break;
        if (deleteIndex === null) {
          triggerDeleteMode();
          break;
        }
        setDeleteIndex((prev) => (prev === 0 ? 0 : prev - 1));
        break;

      case KeyEventValues.ARROW_RIGHT.key:
        if (!isInitial || deleteIndex === null) {
          break;
        }
        if (deleteIndex === tags.length - 1) {
          triggerDeleteMode();
        }
        setDeleteIndex(deleteIndex + 1);
        break;

      case KeyEventValues.BACKSPACE.key:
        if (!isInitial || tags.length === 0) {
          setValidationError('');
          break;
        }
        if (deleteIndex === null) {
          triggerDeleteMode();
          setValidationError('');
          break;
        }
        setValidationError('');
        handleDelete(tags[deleteIndex].id);
        break;

      case KeyEventValues.COMMA.key:
      case KeyEventValues.ENTER.key:
        if (inputValue.trim()) {
          handleOnSelect(inputValue, true);
          event.stopPropagation();
        }
        break;

      default:
        setDeleteIndex(null);
        setValidationError('');
        break;
    }
  };

  // To prevent the focus from being lost when an option is chosen.
  // This is due to the contextMenu focusing the first dropdown item.
  const handleInputOnClick = () => {
    setContextMenuItems(suggestions.filter(({ id }) => (
      !tags.find(({ id: tagId }) => tagId === id))));
    setTimeout(() => {
      inputRef.current.focus();
    }, FOCUS_TIMER_INTERVAL);
  };

  const handleClose = () => {
    openState.current = false;
  };

  return (
    <div className={clsx('tag-input-cont', { disabled: isDisabled })} ref={ref}>
      <div
        tabIndex={0}
        ref={wrapperRef}
        onKeyDown={handleKeyDown}
        className={clsx('tic-context-menu', className)}
      >
        {label && <span className="tcm-label">{getTranslation(label)}</span>}
        <TagWithSuggestions
          {...props}
          tags={tags}
          value={value}
          ref={inputRef}
          maxItems={maxItems}
          disabled={isDisabled}
          onDelete={handleDelete}
          items={contextMenuItems}
          deleteIndex={deleteIndex}
          onSelect={handleOnSelect}
          labelAccessor={labelAccessor}
          validationError={validationError}
          inputOnClick={handleInputOnClick}
          inputOnChange={handleInputOnChange}
          onClose={handleClose}
          rightAddOn={rightAddOn}
        />
      </div>
      <AnimateHeight>
        {!isDisabled && (
          <div className={clsx('tic-helper-label', { error: !!validationError })}>
            {validationError ? getTranslation(validationError)
              : getTranslation(helperText)}
          </div>
        )}
      </AnimateHeight>
    </div>
  );
});

TagInput.defaultProps = {
  tags: [],
  label: '',
  className: '',
  onAdd: () => {},
  suggestions: [],
  validations: [],
  isDisabled: false,
  memorizeTags: true,
  onDelete: () => {},
  maxItems: MAX_ITEMS,
  caseSensitive: false,
  allowTagCreation: false,
  validationErrorMessages: {},
  regexMessageMap: {},
  helperText: 'USE_COMMAS_TO_SEPARATE_ITEMS',
  idAccessor: DEFAULT_ID_ACCESSOR,
  maxLabelLength: MAX_LABEL_LENGTH,
  labelAccessor: DEFAULT_LABEL_ACCESSOR,
  rightAddOn: null,
};

TagInput.propTypes = {
  onAdd: PropTypes.func,
  label: PropTypes.string,
  onDelete: PropTypes.func,
  isDisabled: PropTypes.bool,
  maxItems: PropTypes.number,
  className: PropTypes.string,
  // Show last created tag as suggestion after delete
  memorizeTags: PropTypes.bool,
  idAccessor: PropTypes.string,
  labelAccessor: PropTypes.string,
  maxLabelLength: PropTypes.number,
  allowTagCreation: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  validationErrorMessages: PropTypes.shape({
    text_duplicate_item: PropTypes.string || undefined,
    max_items: PropTypes.string || undefined,
  }),
  regexMessageMap: PropTypes.shape({}),
  helperText: PropTypes.string,
  validations: PropTypes.arrayOf(PropTypes.string),
  suggestions: PropTypes.arrayOf(PropTypes.shape({})),
  caseSensitive: PropTypes.bool,
  rightAddOn: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default memo(TagInput);
